import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, MapPin } from "lucide-react";
import { HERO, BOOKING_URL } from "../../mock/mock";
import { SplitWords } from "../motionHelpers";
import { useSettings } from "../useSiteData";
import { resolveImg } from "../adminApi";

export default function Hero() {
  const ref = useRef(null);
  const settings = useSettings();
  const h = settings?.hero || {};
  const heroImage = resolveImg(h.image || HERO.image);
  const line1 = h.line1 || "Now";
  const line2 = h.line2 || "Serving!";
  const badge = h.badge || "Now Open · Sucat, Parañaque";
  const subtext = h.subtext || HERO.subtext;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scaleBg = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const yContent = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[#06170f]">
      {/* Full-bleed hero image */}
      <motion.img
        src={heroImage}
        alt="Pickleball player at The Supreme Courts"
        style={{ y: yBg, scale: scaleBg }}
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      {/* Legibility gradients */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#06170f]/95 via-[#06170f]/45 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#06170f] via-transparent to-[#06170f]/50" />

      {/* Content */}
      <motion.div style={{ y: yContent, opacity: fade }} className="relative z-10 h-full max-w-[1400px] mx-auto px-5 lg:px-8 flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7 }}
          className="inline-flex items-center gap-2 border border-sc-lime/50 bg-black/20 backdrop-blur rounded-full px-4 py-1.5 mb-6 w-fit">
          <span className="w-2 h-2 rounded-full bg-sc-lime animate-pulse" />
          <span className="font-grotesk uppercase tracking-[0.25em] text-xs text-sc-lime">{badge}</span>
        </motion.div>

        <h1 className="font-display uppercase leading-[0.82] text-[19vw] lg:text-[13rem] text-white drop-shadow-[0_10px_40px_rgba(0,0,0,0.5)]">
          <SplitWords text={line1} className="block" wordClass="d2-shine" />
          <SplitWords text={line2} className="block" wordClass="text-sc-lime" delay={0.2} />
        </h1>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.8 }}
          className="max-w-xl text-lg md:text-xl text-white/85 mt-6 font-grotesk">
          {subtext}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }}
          className="flex flex-wrap items-center gap-4 mt-9">
          <a href={BOOKING_URL} className="d2-btn px-9 py-4 text-sm inline-flex items-center gap-2">Book Now <ArrowRight className="w-4 h-4" /></a>
          <Link to="/courts/pickleball" className="d2-btn-ghost px-8 py-4 text-sm inline-flex items-center gap-2">Explore the Courts</Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 0.8 }}
          className="flex items-center gap-3 mt-9 text-white/70 font-grotesk text-sm">
          <MapPin className="w-4 h-4 text-sc-lime" /> 200 Leonardo St, Vitalez Compound · Sucat, Parañaque
        </motion.div>
      </motion.div>

      {/* scroll hint */}
      <motion.div style={{ opacity: fade }} className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
        <span className="font-grotesk uppercase tracking-[0.3em] text-[10px] text-white/60">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.6, repeat: Infinity }}>
          <ArrowDown className="w-4 h-4 text-sc-lime" />
        </motion.div>
      </motion.div>
    </section>
  );
}
